"use client";

import type {
  PlaceDetailsResponse,
  PlacePrediction,
  PlacesAutocompleteResponse,
} from "@/app/api/places/autocomplete/route";
import { useCallback, useEffect, useRef, useState } from "react";

interface UsePlacesAutocompleteOptions {
  /** Debounce delay in milliseconds (default: 300) */
  debounceMs?: number;
  /** Minimum characters before searching (default: 2) */
  minChars?: number;
}

interface UsePlacesAutocompleteReturn {
  /** Current search input value */
  inputValue: string;
  /** Set the search input value */
  setInputValue: (value: string) => void;
  /** Place suggestions from the API */
  suggestions: PlacePrediction[];
  /** Whether a search is in progress */
  isLoading: boolean;
  /** Whether the input is being debounced before API call */
  isDebouncing: boolean;
  /** Error message if search failed */
  error: string | null;
  /** Clear suggestions and input */
  clear: () => void;
  /** Get coordinates for a selected place */
  getPlaceDetails: (placeId: string) => Promise<PlaceDetailsResponse | null>;
  /** Whether details are being fetched */
  isLoadingDetails: boolean;
}

/**
 * Hook for Google Places Autocomplete functionality.
 * Provides debounced search and place details fetching.
 */
export function usePlacesAutocomplete(
  options: UsePlacesAutocompleteOptions = {},
): UsePlacesAutocompleteReturn {
  const { debounceMs = 300, minChars = 2 } = options;

  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState<PlacePrediction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDebouncing, setIsDebouncing] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Session token for Google Places billing optimization
  const sessionTokenRef = useRef<string>(generateSessionToken());
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Fetch suggestions from API
  const fetchSuggestions = useCallback(
    async (query: string) => {
      if (query.length < minChars) {
        setSuggestions([]);
        setError(null);
        return;
      }

      // Cancel any pending request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();
      setIsLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({
          input: query,
          sessionToken: sessionTokenRef.current,
        });

        const basePath = process.env.NEXT_PUBLIC_BASE_PATH || "";
        const response = await fetch(
          `${basePath}/api/places/autocomplete?${params}`,
          {
            signal: abortControllerRef.current.signal,
          },
        );

        if (!response.ok) {
          throw new Error("Failed to fetch suggestions");
        }

        const data: PlacesAutocompleteResponse = await response.json();
        setSuggestions(data.predictions);
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") {
          // Request was cancelled, ignore
          return;
        }
        setError("Failed to fetch suggestions");
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    },
    [minChars],
  );

  // Debounced search effect
  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    if (inputValue.length < minChars) {
      // Also abort any in-flight request when input drops below minChars
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      setSuggestions([]);
      setError(null);
      setIsLoading(false);
      setIsDebouncing(false);
      return;
    }

    setIsDebouncing(true);
    debounceTimeoutRef.current = setTimeout(() => {
      setIsDebouncing(false);
      fetchSuggestions(inputValue);
    }, debounceMs);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [inputValue, debounceMs, minChars, fetchSuggestions]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const clear = useCallback(() => {
    setInputValue("");
    setSuggestions([]);
    setError(null);
    setIsDebouncing(false);
    // Generate new session token for next search session
    sessionTokenRef.current = generateSessionToken();
  }, []);

  const getPlaceDetails = useCallback(
    async (placeId: string): Promise<PlaceDetailsResponse | null> => {
      setIsLoadingDetails(true);
      setError(null);

      try {
        const basePath = process.env.NEXT_PUBLIC_BASE_PATH || "";
        const response = await fetch(`${basePath}/api/places/autocomplete`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            placeId,
            sessionToken: sessionTokenRef.current,
          }),
        });

        if (!response.ok) {
          throw new Error("Failed to fetch place details");
        }

        const data: PlaceDetailsResponse = await response.json();

        // Generate new session token after successful place selection
        sessionTokenRef.current = generateSessionToken();

        return data;
      } catch {
        setError("Failed to fetch place details");
        return null;
      } finally {
        setIsLoadingDetails(false);
      }
    },
    [],
  );

  return {
    inputValue,
    setInputValue,
    suggestions,
    isLoading,
    isDebouncing,
    error,
    clear,
    getPlaceDetails,
    isLoadingDetails,
  };
}

/**
 * Generate a unique session token for Google Places API
 * Session tokens group autocomplete requests for billing purposes
 */
function generateSessionToken(): string {
  return crypto.randomUUID();
}
